/**
 * Centralized Query Keys
 *
 * Factory functions for React Query cache keys.
 * Using factories ensures consistent key structures and enables
 * type-safe invalidation patterns.
 *
 * Pattern: Each domain has an `all` base key that allows wildcard invalidation,
 * with specific keys building on top of it.
 */

// ============================================================================
// Project Keys
// ============================================================================

export const projectKeys = {
  all: ['projects'] as const,
  /**
   * The caller's own projects. Pass `includeHealth` when the response carries the
   * `health` field, and the key gains a segment for it.
   *
   * That segment is load-bearing rather than tidy. Two components asked for this key
   * with two different endpoints — `CustomerDashboard` for `/projects` and
   * `useMyProjects` (via `TaskFilters`, which that same page renders) for
   * `/projects/my`. React Query runs one `queryFn` per key, the first observer's, and
   * effects run child before parent: the page's own request was never sent, and it
   * rendered the other endpoint's body. `/projects/my` carries no `health` at all, so
   * asking for health without splitting the key cannot work.
   *
   * Absent when false, so every existing caller keeps the key it had. Partial matching
   * means an `invalidateQueries` on `my(tenantId)` still reaches both variants.
   */
  my: (tenantId?: string | null, includeHealth = false) =>
    [...projectKeys.all, 'my', tenantId ?? null, ...(includeHealth ? ['health'] : [])] as const,
  list: (tenantId?: string) => [...projectKeys.all, 'list', tenantId] as const,
  detail: (projectId: string) => [...projectKeys.all, 'detail', projectId] as const,
  health: (projectId: string) => [...projectKeys.all, 'health', projectId] as const,
  members: (projectId: string) => [...projectKeys.all, 'members', projectId] as const,
  assignableUsers: (projectId: string) =>
    [...projectKeys.all, 'assignable-users', projectId] as const,
  eligibleMembers: (projectId: string) =>
    [...projectKeys.all, 'eligible-members', projectId] as const,
  updates: (projectId: string) => [...projectKeys.all, 'updates', projectId] as const,
  updateComments: (projectId: string, updateId: string) =>
    [...projectKeys.updates(projectId), 'comments', updateId] as const,
  analytics: (projectId: string, startDate?: string, endDate?: string, testPlanId?: string) =>
    [...projectKeys.detail(projectId), 'analytics', startDate, endDate, testPlanId] as const,
};

// ============================================================================
// Issue Keys
// ============================================================================

export const issueKeys = {
  all: ['issues'] as const,
  list: (projectId: string) => [...issueKeys.all, 'list', projectId] as const,
  detail: (issueId: string) => [...issueKeys.all, 'detail', issueId] as const,
  comments: (issueId: string) => [...issueKeys.detail(issueId), 'comments'] as const,
  attachments: (issueId: string) => [...issueKeys.detail(issueId), 'attachments'] as const,
  activity: (issueId: string) => [...issueKeys.detail(issueId), 'activity'] as const,
  activityList: (issueId: string, sort: 'asc' | 'desc') =>
    [...issueKeys.activity(issueId), sort] as const,
  watchers: (issueId: string) => [...issueKeys.detail(issueId), 'watchers'] as const,
  externalLinks: (issueId: string) => [...issueKeys.detail(issueId), 'external-links'] as const,
};

// ============================================================================
// User Keys
// ============================================================================

export const userKeys = {
  all: ['users'] as const,
  me: () => [...userKeys.all, 'me'] as const,
  available: (projectId: string) => [...userKeys.all, 'available', projectId] as const,
  list: (tenantId?: string) => [...userKeys.all, 'list', tenantId] as const,
  detail: (userId: string) => [...userKeys.all, 'detail', userId] as const,
};

// ============================================================================
// Notification Keys
// ============================================================================

export const notificationKeys = {
  all: ['notifications'] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  list: () => [...notificationKeys.all, 'list'] as const,
};

// ============================================================================
// Tenant Keys
// ============================================================================

export const tenantKeys = {
  all: ['tenants'] as const,
  list: () => [...tenantKeys.all, 'list'] as const,
  detail: (tenantId: string) => [...tenantKeys.all, 'detail', tenantId] as const,
};

// ============================================================================
// Dashboard Keys
// ============================================================================

export const dashboardKeys = {
  all: ['dashboard'] as const,
  metrics: (tenantId?: string | null) => [...dashboardKeys.all, 'metrics', tenantId] as const,
  customerMetrics: (tenantId?: string | null) =>
    [...dashboardKeys.all, 'customer-metrics', tenantId] as const,
  bct: (tenantId?: string | null, workLimit?: number) =>
    [...dashboardKeys.all, 'bct', tenantId, workLimit] as const,
};

// ============================================================================
// Search Keys
// ============================================================================

export const searchKeys = {
  all: ['search'] as const,
  results: (query: string) => [...searchKeys.all, query] as const,
};

// ============================================================================
// Test Plan Keys
// ============================================================================

export const testPlanKeys = {
  all: ['test-plans'] as const,
  list: (projectId: string) => [...testPlanKeys.all, 'list', projectId] as const,
  detail: (testPlanId: string) => [...testPlanKeys.all, 'detail', testPlanId] as const,
  runs: (testPlanId: string) => [...testPlanKeys.detail(testPlanId), 'runs'] as const,
};

// ============================================================================
// Test Run Keys
// ============================================================================

export const testRunKeys = {
  all: ['test-runs'] as const,
  detail: (runId: string) => [...testRunKeys.all, 'detail', runId] as const,
  // Nested under detail on purpose: the runner reads a run and its progress as two
  // queries over the same row, so invalidating the detail key refreshes both and no
  // caller has to remember the second one.
  progress: (runId: string) => [...testRunKeys.detail(runId), 'progress'] as const,
};

// Outside testRunKeys on purpose: recording a verdict invalidates the run, and a stored
// screenshot never changes, so its bytes must not be fetched again with every verdict.
export const executionAttachmentKeys = {
  all: ['execution-attachments'] as const,
  blob: (attachmentId: string) => [...executionAttachmentKeys.all, attachmentId] as const,
};

// ============================================================================
// Document Keys
// ============================================================================

export const documentKeys = {
  all: ['documents'] as const,
  list: (projectId: string, folderId?: string) =>
    [...documentKeys.all, 'list', projectId, folderId] as const,
  detail: (documentId: string) => [...documentKeys.all, 'detail', documentId] as const,
  folders: (projectId: string) => [...documentKeys.all, 'folders', projectId] as const,
};

// ============================================================================
// Time Entry Keys
// ============================================================================

export const timeEntryKeys = {
  all: ['time-entries'] as const,
  list: (projectId?: string, userId?: string) =>
    [...timeEntryKeys.all, 'list', projectId, userId] as const,
  detail: (entryId: string) => [...timeEntryKeys.all, 'detail', entryId] as const,
};

// ============================================================================
// Announcement Keys
// ============================================================================

export const announcementKeys = {
  all: ['announcements'] as const,
  unseen: () => [...announcementKeys.all, 'unseen'] as const,
  admin: () => [...announcementKeys.all, 'admin'] as const,
};

// ============================================================================
// Analytics Keys
// ============================================================================

export const analyticsKeys = {
  all: ['analytics'] as const,
  onboarding: () => [...analyticsKeys.all, 'onboarding'] as const,
};

// ============================================================================
// User Activity Keys
// ============================================================================

export const userActivityKeys = {
  all: ['user-activity'] as const,
  list: (userId: string, limit?: number) =>
    [...userActivityKeys.all, 'list', userId, limit] as const,
};

// ============================================================================
// Task Keys (Unified Tasks)
// ============================================================================

export const taskKeys = {
  all: ['tasks'] as const,
  list: (filters: Record<string, unknown>) => [...taskKeys.all, 'list', filters] as const,
};
